import React, {MutableRefObject, useCallback, useEffect} from 'react'
import {createPortal} from 'react-dom'

import {InternalLinkOption} from "./option"
import Popover from "../../../gd-popover/popover"
import SlashDefaultMenu from "./components/default/default-content"
import SlashSearchMenu from "./components/search/search-content"
import {mergeRegister} from "@lexical/utils"
import {
    KEY_ARROW_DOWN_COMMAND,
    KEY_ARROW_UP_COMMAND,
    COMMAND_PRIORITY_NORMAL
} from "lexical"
import {useLexicalComposerContext} from "@lexical/react/LexicalComposerContext"
import {useForceUpdate} from "../../../../gd/utils/dom-and-react/react-utils"

const debug = false
// const debug = "SlashMenu"

export function SlashMenu (
    anchorElementRef: MutableRefObject<HTMLElement | null>,
    itemProps       : {
        selectedIndex           : number | null
        selectOptionAndCleanUp  : (option: InternalLinkOption) => void
        setHighlightedIndex     : (index: number) => void
        options                 : Array<InternalLinkOption>
    },
    matchingString  : string | null
) {
    const [editor] = useLexicalComposerContext()

    const forceUpdate = useForceUpdate()

    useEffect(() => {
        let observer: MutationObserver

        // anchorElementRef.current is updated by LexicalTypeaheadMenuPlugin in unknown moment of time
        // in order to get latest node position we should listen to anchorElementRef.current mutation to register when
        // its style is changed and update popover position accordingly
        if (anchorElementRef.current) {
            observer = new MutationObserver(mutations => {
                debug && console.log(debug, 'MutationObserver', mutations)
                forceUpdate()
            })
            observer.observe(anchorElementRef.current, {
                attributes          : true,
                attributeFilter     : ['style'],
                attributeOldValue   : true,
            })
        }

        return ()=> {
            observer?.disconnect()
        }
    }, [anchorElementRef.current])

    debug && console.log(debug, 'render', matchingString)

    useEffect(() => {
        const unregister = mergeRegister(
            editor.registerCommand(KEY_ARROW_DOWN_COMMAND, payload => {
                let newIndex    = itemProps.selectedIndex ? itemProps.selectedIndex : 0
                let option      = undefined
                const length    = itemProps.options.length
                const event     = payload

                while(!option?.id && length > newIndex) {
                    newIndex    = newIndex + 1
                    option      = itemProps.options[newIndex]
                }

                if (!option?.id) {
                    newIndex = _.findLastIndex(itemProps.options, o=>!!o?.id)
                }

                debug && console.log(debug, 'KEY_ARROW_DOWN_COMMAND', newIndex, option, itemProps.selectedIndex)

                itemProps.setHighlightedIndex(newIndex)

                event.preventDefault()
                event.stopImmediatePropagation()

                return true
            }, COMMAND_PRIORITY_NORMAL),
            editor.registerCommand(KEY_ARROW_UP_COMMAND, payload => {
                let newIndex    = itemProps.selectedIndex ? itemProps.selectedIndex : 0
                let option      = undefined
                const event     = payload

                while(!option?.id && newIndex > 0) {
                    newIndex =  newIndex - 1
                    option = itemProps.options[newIndex]
                }

                if (!option?.id) {
                    newIndex = _.findIndex(itemProps.options, o=>!!o?.id)
                }

                debug && console.log(debug, 'KEY_ARROW_UP_COMMAND', newIndex, option, itemProps.selectedIndex)

                itemProps.setHighlightedIndex(newIndex)

                event.preventDefault()
                event.stopImmediatePropagation()

                return true
            }, COMMAND_PRIORITY_NORMAL)
        )

        return () => {
            unregister()
        }
    }, [itemProps.selectedIndex])

    const onClick = useCallback((index:number, option:InternalLinkOption) => {
        debug && console.log(debug, 'onClick', index, option)

        itemProps.setHighlightedIndex(index)
        itemProps.selectOptionAndCleanUp(option)
    }, [itemProps])

    const onPopoverHide = useCallback(()=>{
        if (anchorElementRef.current) {
            gd.utils.Dom.removeClass(anchorElementRef.current,"visible")
        }
    }, [])

    if (anchorElementRef.current) {
        gd.utils.Dom.addClass(anchorElementRef.current,"visible")
    }

    // return should be after all hooks, otherwise error occurs on rerender ("Rendered fewer hooks than expected")
    if (!anchorElementRef.current || !itemProps.options.length) return null

    const showSearchMenu = !!matchingString

    debug && console.log(debug, 'render', itemProps.selectedIndex)

    return createPortal(
        <Popover attachment         = "top center"
                 targetAttachment   = "bottom center"
                 attachmentType     = "horizontal"
                 onPopoverHide      = {onPopoverHide}
                 getTargetFunc      = {()=>anchorElementRef.current?.childNodes[0]}>
            {!showSearchMenu &&
                <SlashDefaultMenu options        = {itemProps.options}
                                  selectedIndex  = {itemProps.selectedIndex}
                                  onClick        = {onClick}/>}

            {showSearchMenu &&
                <SlashSearchMenu options        = {itemProps.options}
                                 selectedIndex  = {itemProps.selectedIndex}
                                 onClick        = {onClick}/>}
        </Popover>,
        anchorElementRef.current,
    )
}
